import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, CheckCircle2, ArrowUpRight } from "lucide-react";
import { PageHero, SectionLabel } from "@/components/Bits";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { COMPANY } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXPECT = [
  "A focused conversation about your agency and goals",
  "A look at how the CCA system would apply to your market",
  "Clear, no-pressure next steps — no obligation",
];

export default function Schedule() {
  const [calendlyUrl, setCalendlyUrl] = useState("");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetch(`${API}/config`)
      .then((r) => r.json())
      .then((d) => setCalendlyUrl(d.calendly_url || ""))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  return (
    <div data-testid="schedule-page">
      <Seo
        title="Schedule Consultation"
        description="Book a discovery consultation with CCA to see how disciplined commercial insurance pipeline development fits your agency."
        path="/schedule"
      />
      <PageHero
        eyebrow="Schedule Consultation"
        title="Book a discovery consultation."
        intro="A short, professional conversation to see whether disciplined pipeline development is the right fit for your agency."
      />

      <section className="bg-paper py-16 md:py-24">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-14 px-6 md:grid-cols-12 md:px-12 lg:px-16">
          <Reveal className="md:col-span-4">
            <SectionLabel num="/">What to expect</SectionLabel>
            <ul className="mt-8 space-y-5">
              {EXPECT.map((e) => (
                <li key={e} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-navy" strokeWidth={1.5} />
                  <span className="leading-relaxed text-subtext">{e}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 border-t border-line pt-6">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-subtext">Prefer email?</p>
              <a href={`mailto:${COMPANY.email}`} className="mt-2 block text-lg text-ink hover:text-navy">
                {COMPANY.email}
              </a>
            </div>
          </Reveal>

          <Reveal className="md:col-span-8" delay={0.1}>
            {loaded && calendlyUrl ? (
              <div className="overflow-hidden border border-line bg-white" data-testid="calendly-embed">
                <iframe
                  title="Schedule a consultation"
                  src={calendlyUrl}
                  className="h-[720px] w-full"
                  frameBorder="0"
                />
              </div>
            ) : (
              <div
                data-testid="calendly-placeholder"
                className="flex min-h-[420px] flex-col items-start justify-center border border-line bg-white p-10 md:p-14"
              >
                <CalendarClock className="h-10 w-10 text-navy" strokeWidth={1.25} />
                <h2 className="mt-6 font-display text-2xl font-medium tracking-tight text-ink md:text-3xl">
                  Live scheduling is being connected.
                </h2>
                <p className="mt-4 max-w-md leading-relaxed text-subtext">
                  Our online booking calendar is coming online shortly. In the meantime, send us a
                  note and we'll reach out to schedule your consultation directly.
                </p>
                <Link
                  to="/contact"
                  data-testid="schedule-contact-fallback"
                  className="group mt-8 inline-flex items-center gap-2 border border-ink bg-ink px-7 py-3.5 font-mono text-[12px] uppercase tracking-[0.12em] text-paper transition-colors hover:bg-transparent hover:text-ink"
                >
                  Request a Consultation
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
